import type { ReactNode } from "react";
import { CarbonsixIcon, ElevatedSurface, Spinner, Text } from "@carbon6/c6-frontend-design";
import { hatcheryTextModes } from "@/app/theme";
import styles from "./AuthCard.module.css";

// 카드 그림자만 DS elevation 기본값보다 진하게 override 한다. c6 의 떠 있는 면 그림자를 쓴다.
// 이 그림자는 elevation 2 의 `0 3px 6px` 보다 크고, 다크에서 α 가 따로 커진다.
// styled-components(ElevatedSurface)의 box-shadow 보다 우선하도록 클래스가 아닌 inline style 로
// 준다. 그래서 !important 를 쓰지 않는다.
const CARD_SHADOW = "var(--c6-elevation-shadow-overlay)";

/** 로그인/콜백 흐름의 인라인 상태 한 줄이다. 진행 중에는 스피너, 실패에는 danger 텍스트를 표시한다. */
export type AuthCardStatus =
  | { kind: "idle" }
  | { kind: "pending"; message: string }
  | { kind: "failed"; message: string };

type AuthCardProps = {
  titleId?: string;
  /** 부트 스플래시처럼 타이틀 없이 상태만 표시할 때는 생략한다. */
  title?: ReactNode;
  subtitle?: ReactNode;
  status?: AuthCardStatus;
  /** 카드 하단 액션(예: 로그인 버튼, 돌아가기 버튼). */
  children?: ReactNode;
};

/**
 * 인증 흐름 공용 카드다. 로그인(`LandingPage`)과 OAuth 콜백(`AuthCallbackPage`)이 같은
 * 카드·상태줄을 쓰도록 셸을 한 곳에 둔다. 콜백은 별도 라우트(`/auth/callback`)다. 하지만
 * 화면은 로그인 카드와 같게 보여서 끊김 없이 이어진다.
 */
export function AuthCard({ titleId, title, subtitle, status, children }: AuthCardProps) {
  const activeStatus = status && status.kind !== "idle" ? status : null;

  return (
    <main className={styles.page}>
      <ElevatedSurface
        as="section"
        elevation={2}
        hoverElevation={false}
        className={styles.panel}
        style={{ boxShadow: CARD_SHADOW }}
        aria-labelledby={titleId}
      >
        <CarbonsixIcon size={48} className={styles.brandIcon} />
        {title || subtitle ? (
          <div className={styles.header}>
            {title ? (
              <Text variant={hatcheryTextModes["title-bold"]} id={titleId} className={styles.title}>
                {title}
              </Text>
            ) : null}
            {subtitle ? (
              <Text variant={hatcheryTextModes["caption-normal"]} className={styles.subtitle}>
                {subtitle}
              </Text>
            ) : null}
          </div>
        ) : null}
        {/* status prop 이 있으면 상태 슬롯은 idle 이어도 항상 공간을 예약한다.
            그래서 메시지가 나타나도 카드 높이와 버튼 위치가 바뀌지 않는다. */}
        {status ? (
          <div className={styles.status} aria-live="polite">
            {activeStatus?.kind === "pending" ? (
              <span className={styles.statusSpinner}>
                <Spinner size="md" />
              </span>
            ) : null}
            {activeStatus ? (
              <Text
                variant={hatcheryTextModes["caption-normal"]}
                className={
                  activeStatus.kind === "failed" ? styles.statusError : styles.statusMessage
                }
              >
                {activeStatus.message}
              </Text>
            ) : null}
          </div>
        ) : null}
        {children}
      </ElevatedSurface>
    </main>
  );
}
